<template>
  <div class="column-filter">
    <div v-if="loading" class="d-flex justify-content-center align-items-center py-4 h-100">
      <v-progress-circular color="primary" indeterminate></v-progress-circular>
    </div>
    <div v-else>
      <h4 class="ma-0" style="font-size: 16px;"><b>Filter</b> <span class="badge badge-primary ml-1"
          style="line-height: inherit;">{{ totalAppliedFilters }}</span></h4>

      <div v-if="filteredFilters.length" class="d-flex flex-wrap mt-2" style="gap: 12px;">
        <v-menu v-for="filter in filteredFilters" :key="filter.name" offset-y transition="slide-y-transition"
          :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <div v-if="!loading && !hasSelectedValue(filter)" class="filter-item" v-bind="attrs" v-on="on">
              <span>{{ getFilterLabel(filter) }}</span>
              <v-icon small color="#3F3F3F">{{ filter.data_type === 'date' ? "mdi-calendar-blank-outline" :
                "mdi-chevron-down"
                }}</v-icon>
            </div>

            <div v-else class="filter-item-selected" v-bind="attrs" v-on="on">
              <div class="item-selected">
                <span>{{ getFilterLabel(filter) }}</span>
                <div class="d-flex justify-space-between">
                  <h5>
                    <b v-if="Array.isArray(filterValues[filter.keys[0]])">
                      <span class="badge badge-primary mr-1" style="line-height: inherit; color: #FFF;">
                        {{ filterValues[filter.keys[0]].length }}
                      </span> Selected
                    </b>
                    <b v-else>
                      {{ getSelectedDisplayValue(filter) }}
                    </b>
                  </h5>
                  <v-icon small color="#3F3F3F">{{ filter.data_type === 'date' ? "mdi-calendar-blank-outline" :
                    "mdi-chevron-down" }}</v-icon>
                </div>
              </div>
              <div class="clear-filter" @click.stop="clearFilterValue(filter)">
                <v-icon small color="#3F3F3F">mdi-close</v-icon>
              </div>
            </div>
          </template>
          <div id="attach-parent"></div>
          <v-card class="px-4 py-2 filter-card" :class="{ 'date-filter-card': filter.data_type === 'date' }"
            style="border-radius: 8px !important; max-width: 332px;">
            <b class="mb-2 d-block" style="font-size: 14px;">{{ getFilterLabel(filter) }}</b>
            <DynamicDateFilter v-if="filter.data_type === 'date'" :filter="filter"
              :radio-value="getDateFilterRadioValue(filter)"
              :presets="getDynamicPresetsForFilter(filter)"
              :dynamic-value="getDynamicFilterValue(filter)"
              :has-custom-config="Boolean(dynamicDateCustomConfig[filter.keys[0]])"
              :custom-value="getCustomDynamicValue(filter)"
              :custom-unit="getCustomDynamicUnit(filter)"
              :custom-max="getCustomDynamicMax(filter)"
              :unit-options="dynamicDateUnitOptions"
              :start-value="filterValues[filter.keys[0]]"
              :end-value="filter.keys[1] ? filterValues[filter.keys[1]] : ''"
              note-text="Dynamic date filters automatically update based on the scheduled send date. Exported data may change each time this report is sent."
              @radio-change="setDateFilterRadioValue"
              @preset-select="selectDynamicPreset"
              @custom-value-input="setCustomDynamicValue"
              @custom-unit-input="setCustomDynamicUnit"
              @start-date-input="$set(filterValues, filter.keys[0], $event)"
              @end-date-input="filter.keys[1] && $set(filterValues, filter.keys[1], $event)" />

            <!-- Multi-Checkbox -->
            <div v-else-if="filter.data_type === 'checkbox'">
              <!-- Grouped View (when parent has multiple values) -->
              <div v-if="Array.isArray(filter.data) && isGroupedFilter(filter)">
                <!-- Select All for grouped departments -->
                <div class="custom-control custom-checkbox mb-2">
                  <input type="checkbox" :id="`${filter.keys[0]}-select-all`"
                    :checked="isAllDepartmentsSelected(filter)"
                    @change="toggleAllDepartments(filter, $event.target.checked)" class="custom-control-input">
                  <label :for="`${filter.keys[0]}-select-all`" class="custom-control-label font-weight-bold">
                    All
                  </label>
                </div>
                <div v-for="(group, groupId) in getGroupedData(filter)" :key="groupId" class="mb-3"
                  v-if="group.items && group.items.length > 0">
                  <div class="parent-group-header"><b>{{ group.parentName }}</b></div>
                  <div v-for="option in group.items" :key="option.id || option.name"
                    class="custom-control custom-checkbox ml-2">
                    <input type="checkbox" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                      v-model="filterValues[filter.keys[0]]" class="custom-control-input"
                      @change="handleCheckboxChange(filter.keys[0], option.id)">
                    <label :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label">
                      {{ option.name || '-' }}
                    </label>
                  </div>
                </div>
              </div>
              <!-- Flat View (default) -->
              <div v-else-if="Array.isArray(filter.data)">
                <!-- Select All for Business Unit -->
                <div v-if="filter.keys[0] === businessUnitKey" class="custom-control custom-checkbox mb-2">
                  <input type="checkbox" :id="`${filter.keys[0]}-select-all`"
                    :checked="isAllBusinessUnitsSelected(filter)"
                    @change="toggleAllBusinessUnits(filter, $event.target.checked)" class="custom-control-input">
                  <label :for="`${filter.keys[0]}-select-all`" class="custom-control-label font-weight-bold">
                    All
                  </label>
                </div>
                <!-- Select All for Department (when 1 BU selected - flat view) -->
                <div v-else-if="filter.keys[0] === departmentKey" class="custom-control custom-checkbox mb-2">
                  <input type="checkbox" :id="`${filter.keys[0]}-select-all`"
                    :checked="isAllDepartmentsSelectedFlat(filter)"
                    @change="toggleAllDepartmentsFlat(filter, $event.target.checked)" class="custom-control-input">
                  <label :for="`${filter.keys[0]}-select-all`" class="custom-control-label font-weight-bold">
                    All
                  </label>
                </div>
                <div v-for="option in filteredData(filter)" :key="option.id || option.name"
                  class="custom-control custom-checkbox"
                  v-if="!(filter.keys[0] === businessUnitKey && option.id === 'all')">
                  <input type="checkbox" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                    v-model="filterValues[filter.keys[0]]" class="custom-control-input"
                    @change="handleCheckboxChange(filter.keys[0], option.id)"
                    :disabled="filter.keys[0] === 'template_ids' && isQuestionnaireSelected">
                  <label v-if="filter.keys[0] === 'sticker_label_ids'" :for="`${filter.keys[0]}-${option.id}`"
                    class="custom-control-label sticker" :style="{ backgroundColor: option.color }">
                    {{ option.name || '-' }}
                  </label>
                  <label v-else-if="filter.keys[0] === 'template_ids' && option.id !== 'all'"
                    :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label"
                    :style="{ backgroundColor: option.color }">
                    #{{ option.id }} {{ option.name || '-' }}
                  </label>
                  <label v-else :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label">
                    {{ option.name || '-' }}
                  </label>
                </div>
              </div>
              <v-autocomplete v-else v-model="filterValues[filter.keys[0]]" :items="dynamicOptions[filter.keys[0]]"
                :search-input.sync="searchInputs[filter.keys[0]]" :menu-props="{ attach: true }" item-value="id"
                item-text="name" label="Search" append-icon="mdi-magnify" solo dense flat hide-details hide-no-data
                multiple small-chips deletable-chips cache-items @update:search-input="fetchFilterData(filter.keys[0])"
                @change="searchInputs[filter.keys[0]] = ''"></v-autocomplete>
            </div>

            <!-- Select Dropdown -->
            <div v-else-if="filter.data_type === 'select'" class="position-relative">
              <div v-for="option in filteredData(filter)" :key="option.id ? option.id : option"
                @click="filterValues[filter.keys[0]] = option.id ? option.id : option"
                class="px-4 py-1 hover-light-blue" style="margin: 0 -16px 0 -16px;" :class="{
                  'bg-blue-active': filterValues[filter.keys[0]] === (option.id ? option.id : option),
                }">
                {{ option.name_code_format ? option.name_code_format : option.name ? option.name : option }}
              </div>
            </div>

            <!-- Radio Buttons -->
            <div v-else-if="filter.data_type === 'radio_button'">
              <div v-for="option in filteredData(filter)" :key="option.id ? option.id : option"
                @click="filterValues[filter.keys[0]] = option.id ? option.id : option"
                class="px-4 py-1 hover-light-blue" style="margin: 0 -16px 0 -16px;" :class="{
                  'bg-blue-active': filterValues[filter.keys[0]] === (option.id ? option.id : option),
                }">
                {{ option.name ? option.name : option }}
              </div>
            </div>

            <!-- Radio -->
            <div v-else-if="filter.data_type === 'radio'">
              <div v-for="option in filteredData(filter)"
                @click="handleRadioChecked(filter.keys[0], option.id ? option.id : option)"
                :key="option.id ? option.id : option" class="px-4 py-1 hover-light-blue"
                style="margin: 0 -16px 0 -16px;" :class="{
                  'bg-blue-active': filterValues[filter.keys[0]] === (option.id ? option.id : option),
                }">
                <input type="radio" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                  v-model="filterValues[filter.keys[0]]" style="opacity:1"
                  @click="handleRadioChecked(filter.keys[0], option.id ? option.id : option)">
                {{ option.name ? option.name : option }}
              </div>

              <!-- custom start date -->
              <div v-if="filterValues[filter.keys[0]] == 'custom'" class="d-flex" style="gap: 16px;">
                <div>
                  <label class="form-label mb-1" style="font-size: 12px;">Start Date</label>
                  <CInput v-model="customStartDate" type="date" placeholder="mm/dd/yyyy"
                    style="width: 142px !important;" />
                </div>
                <div>
                  <label class="form-label mb-1" style="font-size: 12px;">End Date</label>
                  <CInput v-model="customEndDate" type="date" placeholder="mm/dd/yyyy"
                    style="width: 142px !important;" />
                </div>
              </div>
            </div>

            <!-- Default Text Input -->
            <div v-else>
              <input v-model="filterValues[filter.keys[0]]" type="text" class="form-control">
            </div>
          </v-card>
        </v-menu>
      </div>

      <div v-if="hasActiveFilters" class="d-flex action justify-content-end mt-2">
        <button class="text-primary mr-4" @click="clearFilters">Clear Filter</button>
        <CButton :color="'#007BFF'" :disabled="disabledApplyFilter" label="Apply" @click.native="applyFilters">
        </CButton>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CButton from '../../c-button.vue';
import CInput from '../../c-input.vue'
import DynamicDateFilter from '../../report_filters/DynamicDateFilter.vue';
import dynamicDateFilterMixin from '../../../shared/dynamic-date-filter-mixin';
import _ from 'lodash';
import moment from 'moment';
import {
  isDynamicFilter,
  extractDynamicCustomConfig,
  getDynamicFilterLabel,
  resolveDynamicFiltersToAbsolute,
} from '../../../shared/report-filter-helpers';

export default {
  components: { CButton, CInput, DynamicDateFilter },
  mixins: [dynamicDateFilterMixin],

  computed: {
    ...mapGetters({
      reportTemplate: "reportTemplates/reportTemplate",
      appliedFilters: "reportTemplates/appliedFilters",
      selectedColumns: "reportTemplates/selectedColumns",
      managementBusinessDepartment: "reportTemplates/managementBusinessDepartment",
    }),
    isClientReport() {
      return this.reportTemplate?.type === 'Report::Client';
    },
    businessUnitKey() {
      return this.isClientReport ? 'business_unit_id' : 'client_business_unit_id';
    },
    departmentKey() {
      return this.isClientReport ? 'department_id' : 'client_department_id';
    },
    disabledApplyFilter() {
      const isRadio = this.filters.some(item => item.data_type == "radio")
      const isCustom = Object.values(this.filterValues).some(value => value === "custom");
      const isCustomAndRadio = isRadio && isCustom
      const customDateDisabled = isCustomAndRadio && (this.customStartDate == '' || this.customEndDate == '')

      // Disable if BU is selected but department is empty
      const buValues = this.filterValues[this.businessUnitKey];
      const hasBuSelected = buValues && Array.isArray(buValues) && buValues.filter(id => id !== 'all').length > 0;
      const deptValues = this.filterValues[this.departmentKey];
      const hasDeptSelected = deptValues && Array.isArray(deptValues) && deptValues.length > 0;
      const deptRequiredButEmpty = hasBuSelected && !hasDeptSelected;

      const dateFilterInvalid = this.filters.filter(f => f.data_type === 'date').some(f => {
        const key = f.keys[0];
        const mode = this.dateFilterRadioValue[key];
        const config = this.dynamicDateCustomConfig[key];
        if (mode === 'dynamic' && config) return !config.value;
        if (mode === 'absolute') return !this.filterValues[key] || (f.keys.length > 1 && !this.filterValues[f.keys[1]]);
        return false;
      });

      return customDateDisabled || deptRequiredButEmpty || dateFilterInvalid;
    },
    filters() {
      return this.reportTemplate?.filter_options || [];
    },
    filteredFilters() {
      return this.filters.filter(filter => {
        if (filter.data_type === 'date') return true;

        if (filter.keys.includes(this.departmentKey)) {
          // Hide department if no valid BU selected (exclude 'all' from valid selection)
          const buValues = this.filterValues[this.businessUnitKey];
          if (!buValues || !Array.isArray(buValues)) return false;
          const validBuIds = buValues.filter(id => id !== 'all');
          return validBuIds.length > 0;
        }
        if (filter.keys.includes(this.businessUnitKey)) {
          return this.managementBusinessDepartment;
        }
        return true;
      })
    },
    isQuestionnaireSelected() {
      const hasQuestionnaire = this.selectedColumns.some(col => col.group === 'Questionnaire::Template');
      const hasTemplateIds = this.filterValues.template_ids && this.filterValues.template_ids.length > 0;

      return hasQuestionnaire && hasTemplateIds;
    },
    selectedTemplateId() {
      const questionnaireColumn = this.selectedColumns.find(col => col.group === 'Questionnaire::Template');
      return questionnaireColumn ? questionnaireColumn.columnName : null;
    },
    hasActiveFilters() {
      if (Object.values(this.dynamicDateFilterValues).some(value => value)) return true;

      return Object.values(this.filterValues).some(value => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== null && value !== undefined && value !== '';
      });
    },
    totalAppliedFilters() {
      const filters = this.filterValues || {};
      const regularFilterCount = Object.values(filters).reduce((count, value) => {
        if (Array.isArray(value)) {
          const filtered = value.filter(v => v.toLowerCase?.() !== 'all');
          return count + (filtered.length > 0 ? 1 : 0);
        } else if (typeof value === 'string') {
          const val = value.trim().toLowerCase();
          return count + (val !== '' && val !== 'all' ? 1 : 0);
        } else if (value !== null && value !== undefined) {
          return count + 1;
        }
        return count;
      }, 0);
      const dynamicFilterCount = Object.values(this.dynamicDateFilterValues).filter(value => value).length;
      return regularFilterCount + dynamicFilterCount;
    },
  },

  data() {
    return {
      customStartDate: "",
      customEndDate: "",
      filterValues: {},
      searchInputs: {},
      dynamicOptions: {},
      apiEndpoints: {
        contributor_ids: "/api/v1/members",
        client_ids: "/api/v1/clients",
      },
      loading: true,
      initialDeptLoaded: false,
      previousBuSelection: [],
    };
  },

  methods: {
    async initializeFilterValues() {
      // Initialize filterValues directly
      const savedFilter = { ...(this.reportTemplate.filter || {}) };
      this.dateFilterRadioValue = {};
      this.dynamicDateCustomConfig = {};
      this.dynamicDateFilterValues = this.normalizeDynamicFilters(savedFilter.dynamic_filters);
      this.filterValues = { ...savedFilter };
      this.$delete(this.filterValues, 'dynamic_filters');

      this.filters.forEach(filter => {
        const savedFilterValue = savedFilter?.[filter.keys[0]];

        if (filter.data_type === "checkbox") {
          if (Array.isArray(savedFilterValue) && savedFilterValue.length > 0) {
            this.$set(this.filterValues, filter.keys[0], savedFilterValue);
          } else {
            const allOption = filter.data?.find(option => option.id === "all");
            this.$set(this.filterValues, filter.keys[0], allOption ? ["all"] : []);
          }

          if (this.isQuestionnaireSelected) {
            this.$set(this.filterValues, 'template_ids', [this.selectedTemplateId]);
          }
        } else if (filter.data_type === "radio") {
          if (savedFilterValue) {
            if (typeof savedFilterValue === "string" && savedFilterValue.includes("to")) {
              const splitValueDate = savedFilterValue.split(" ")
              this.customStartDate = splitValueDate[0]
              this.customEndDate = splitValueDate[2]
              this.$set(this.filterValues, filter.keys[0], "custom");
            } else {
              this.$set(this.filterValues, filter.keys[0], savedFilterValue);
            }

          } else {
            const allOption = filter.data?.find(option => option.id === "all");
            this.$set(this.filterValues, filter.keys[0], allOption ? "all" : "");
          }
        } else if (filter.data_type === "radio_button") {
          if (savedFilterValue) {
            this.$set(this.filterValues, filter.keys[0], savedFilterValue);
          } else {
            const allOption = filter.data?.find(option => option.id === "all");
            this.$set(this.filterValues, filter.keys[0], allOption ? "all" : "");
          }
        } else if (filter.data_type === "select") {
          if (savedFilterValue) {
            this.$set(this.filterValues, filter.keys[0], savedFilterValue);
          } else {
            const allOption = filter.data?.find(option => option.id === "all");
            this.$set(this.filterValues, filter.keys[0], allOption ? "all" : "");
          }
        } else if (filter.data_type === "date" && filter.keys.length > 1) {
          const savedDynamicValue = this.getDynamicFilterValue(filter);
          if (savedDynamicValue) {
            this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
            const customConfig = extractDynamicCustomConfig(savedDynamicValue);
            if (customConfig) {
              this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
            }
            this.$set(this.filterValues, filter.keys[0], "");
            this.$set(this.filterValues, filter.keys[1], "");
          } else if (savedFilterValue) {
            if (isDynamicFilter(savedFilterValue)) {
              this.$set(this.dynamicDateFilterValues, filter.name, savedFilterValue);
              this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
              const customConfig = extractDynamicCustomConfig(savedFilterValue);
              if (customConfig) {
                this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
              }
              this.$set(this.filterValues, filter.keys[0], "");
              this.$set(this.filterValues, filter.keys[1], "");
            } else {
              this.$set(this.dateFilterRadioValue, filter.keys[0], 'absolute');
              this.$set(this.filterValues, filter.keys[0], savedFilterValue);
              this.$set(this.filterValues, filter.keys[1], savedFilter?.[filter.keys[1]] || "");
            }
          } else {
            this.$set(this.dateFilterRadioValue, filter.keys[0], 'empty');
            this.$set(this.filterValues, filter.keys[0], "");
            this.$set(this.filterValues, filter.keys[1], "");
          }
        } else if (filter.data_type === "date") {
          const savedDynamicValue = this.getDynamicFilterValue(filter);
          if (savedDynamicValue) {
            this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
            const customConfig = extractDynamicCustomConfig(savedDynamicValue);
            if (customConfig) {
              this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
            }
            this.$set(this.filterValues, filter.keys[0], "");
          } else if (savedFilterValue) {
            if (isDynamicFilter(savedFilterValue)) {
              this.$set(this.dynamicDateFilterValues, filter.name, savedFilterValue);
              this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
              const customConfig = extractDynamicCustomConfig(savedFilterValue);
              if (customConfig) {
                this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
              }
              this.$set(this.filterValues, filter.keys[0], "");
            } else {
              this.$set(this.dateFilterRadioValue, filter.keys[0], 'absolute');
              this.$set(this.filterValues, filter.keys[0], savedFilterValue);
            }
          } else {
            this.$set(this.dateFilterRadioValue, filter.keys[0], 'empty');
            this.$set(this.filterValues, filter.keys[0], "");
          }
        } else {
          this.$set(this.filterValues, filter.keys[0], savedFilterValue ?? "");
        }

        if (!filter.data) {
          this.$set(this.dynamicOptions, filter.keys[0], []);
          this.$set(this.searchInputs, filter.keys[0], "");
        }
      });

      await this.fetchInitialFilterData();
      if (this.filterValues[this.businessUnitKey]) {
        // Trigger watcher manually if needed, or rely on immediate: true in watcher option if static
        // However, since we are moving to static watchers pointing to a method, immediate: true will handle it.
        // But for safety if data comes late:
        this.handleBusinessUnitChange(this.filterValues[this.businessUnitKey], this.businessUnitKey, this.departmentKey);
      }
      this.loading = false;
    },
    async fetchInitialFilterData() {
      try {
        const dynamicOptions = await this.$store.dispatch("reportTemplates/fetchFilterData", { filters: this.reportTemplate?.filter_options });
        this.dynamicOptions = dynamicOptions || {};
      } catch (error) {
        console.error("Error fetching initial filter data:", error);
      }
    },
    fetchFilterData: _.debounce(async function (key) {
      const query = this.searchInputs[key]?.trim();
      if (!query) return;

      try {
        const data = await this.$store.dispatch("reportTemplates/fetchFilterData", { key, query });
        if (data) {
          this.$set(this.dynamicOptions, key, data[key]);
        }
      } catch (error) {
        console.error("Error fetching filter data:", error);
      }
    }, 500),
    isGroupedFilter(filter) {
      // Check if filter has parent config and parent has multiple values selected
      if (!filter.parent_key || !filter.parent_filter_key) return false;
      const parentValues = this.filterValues[filter.parent_filter_key];
      return Array.isArray(parentValues) && parentValues.length > 1;
    },
    getFilterLabel(filter) {
      if (['client_business_unit_id', 'client_department_id'].includes(filter.keys[0])) {
        return `${filter.name} (Client)`;
      }
      return filter.name;
    },
    getGroupedData(filter) {
      if (!filter.parent_key || !filter.parent_filter_key) return {};

      const parentFilterKey = filter.parent_filter_key;
      const parentKey = filter.parent_key;
      const parentValues = this.filterValues[parentFilterKey] || [];

      // Get parent filter to get parent names
      const parentFilter = this.filters.find(f => f.keys[0] === parentFilterKey);
      const parentNameMap = {};
      if (parentFilter && Array.isArray(parentFilter.data)) {
        parentFilter.data.forEach(item => {
          parentNameMap[item.id] = item.name;
        });
      }

      // Get data source (dynamic or static)
      let data;
      if (Array.isArray(this.dynamicOptions[filter.keys[0]])) {
        data = this.dynamicOptions[filter.keys[0]];
      } else {
        data = filter.data || [];
      }

      // Filter by selected parent values and exclude "all"
      const filteredData = data.filter(item =>
        item?.id !== "all" && parentValues.includes(item[parentKey])
      );

      // Group by parent key
      const grouped = {};
      filteredData.forEach(item => {
        const parentId = item[parentKey];
        if (!grouped[parentId]) {
          grouped[parentId] = {
            parentName: parentNameMap[parentId] || `Group ${parentId}`,
            items: []
          };
        }
        grouped[parentId].items.push(item);
      });
      return grouped;
    },
    isAllDepartmentsSelected(filter) {
      const groupedData = this.getGroupedData(filter);
      const allDeptIds = [];
      Object.values(groupedData).forEach(group => {
        group.items.forEach(item => allDeptIds.push(item.id));
      });
      const selectedIds = this.filterValues[filter.keys[0]] || [];
      return allDeptIds.length > 0 && allDeptIds.every(id => selectedIds.includes(id));
    },
    toggleAllDepartments(filter, checked) {
      const groupedData = this.getGroupedData(filter);
      const allDeptIds = [];
      Object.values(groupedData).forEach(group => {
        group.items.forEach(item => allDeptIds.push(item.id));
      });
      if (checked) {
        this.$set(this.filterValues, filter.keys[0], [...allDeptIds]);
      } else {
        this.$set(this.filterValues, filter.keys[0], []);
      }
    },
    isAllBusinessUnitsSelected(filter) {
      const data = filter.data || [];
      const allBuIds = data.filter(item => item.id !== 'all').map(item => item.id);
      const selectedIds = this.filterValues[filter.keys[0]] || [];
      return allBuIds.length > 0 && allBuIds.every(id => selectedIds.includes(id));
    },
    toggleAllBusinessUnits(filter, checked) {
      const data = filter.data || [];
      const allBuIds = data.filter(item => item.id !== 'all').map(item => item.id);
      if (checked) {
        this.$set(this.filterValues, filter.keys[0], [...allBuIds]);
      } else {
        this.$set(this.filterValues, filter.keys[0], []);
      }
    },
    // Methods for Department All checkbox in Flat View (when 1 BU selected)
    isAllDepartmentsSelectedFlat(filter) {
      const data = this.dynamicOptions[this.departmentKey] || [];
      const allDeptIds = data.filter(item => item.id !== 'all').map(item => item.id);
      const selectedIds = this.filterValues[filter.keys[0]] || [];
      return allDeptIds.length > 0 && allDeptIds.every(id => selectedIds.includes(id));
    },
    toggleAllDepartmentsFlat(filter, checked) {
      const data = this.dynamicOptions[this.departmentKey] || [];
      const allDeptIds = data.filter(item => item.id !== 'all').map(item => item.id);
      if (checked) {
        this.$set(this.filterValues, filter.keys[0], [...allDeptIds]);
      } else {
        this.$set(this.filterValues, filter.keys[0], []);
      }
    },
    filteredData(filter) {
      let data;

      if (filter.keys[0] === this.departmentKey && Array.isArray(this.dynamicOptions[this.departmentKey])) {
        data = this.dynamicOptions[this.departmentKey];
      } else {
        data = filter.data;
      }

      return (data || []).filter(option => option?.id !== "all");
    },
    handleRadioChecked(filterKey, option) {
      if (this.filterValues[filterKey]) {
        this.filterValues[filterKey] = option
      }
    },
    handleCheckboxChange(filterKey, selectedId) {
      if (!this.filterValues[filterKey]) {
        this.$set(this.filterValues, filterKey, []);
      }

      let selectedValues = this.filterValues[filterKey];

      if (selectedId === "all") {
        this.$set(this.filterValues, filterKey, ["all"]);
      } else {
        this.filterValues[filterKey] = selectedValues.filter(id => id !== "all");
      }
    },
    applyFilters() {
      const previousFilters = this.appliedFilters || {};

      const filteredFilters = Object.fromEntries(
        Object.entries(this.filterValues).map(([key, value]) => {
          if (key === 'dynamic_filters') return null;
          if (Array.isArray(value)) {
            const filteredValue = value.filter(item => item !== "all");
            return filteredValue.length > 0 ? [key, filteredValue] : [key, []];
          } else if (typeof value === "string") {
            if (value === "all") {
              if (key === this.businessUnitKey) {
                this.$set(this.filterValues, this.businessUnitKey, "");
                this.$set(this.filterValues, this.departmentKey, []);
                return [key, ""];
              }
              return previousFilters.hasOwnProperty(key) ? [key, ""] : null;
            } else if (value == "custom") {
              const startDateCustom = moment(this.customStartDate).format("YYYY-MM-DD")
              const endDateCustom = moment(this.customEndDate).format("YYYY-MM-DD")
              value = `${startDateCustom} to ${endDateCustom}`
            }
            if (value === "") {
              return null;
            }
          }
          return [key, value];
        }).filter(Boolean)
      );
      filteredFilters.dynamic_filters = this.buildDynamicFiltersPayload();

      const apiParams = resolveDynamicFiltersToAbsolute(filteredFilters, this.filters);

      const payload = {
        id: this.reportTemplate.id,
        payload: {
          report_template: {
            filter: filteredFilters,
          }
        }
      }

      this.$store.dispatch('reportTemplates/updateReportTemplate', payload);
      setTimeout(() => {
        this.$store.dispatch('reportTemplates/getPreviewDataIds', { id: this.reportTemplate.id, params: apiParams });
        this.$store.dispatch('reportTemplates/getDataCount', { id: this.reportTemplate.id, params: apiParams });
      }, 500)
      this.$emit("close-menu");
    },
    clearFilters() {
      this.filters.forEach(filter => {
        if (filter.data_type === "checkbox") {
          const allOption = filter.data?.find(option => option.id === "all");
          this.$set(this.filterValues, filter.keys[0], allOption ? ["all"] : []);
        } else if (filter.data_type === "radio_button" || filter.data_type === "select") {
          const allOption = filter.data?.find(option => option.id === "all");
          this.$set(this.filterValues, filter.keys[0], allOption ? "all" : "");
        } else if (filter.data_type === "radio") {
          if (filter.keys[0] == "filed_date") {
            const savedFilterValue = this.filterValues[filter.keys[0]];
            this.$set(this.filterValues, filter.keys[0], "all");

            if (savedFilterValue) {
              if (typeof savedFilterValue === "string" && savedFilterValue == "custom") {
                this.$set(this.filterValues, filter.keys[0], "");
                this.customStartDate = ""
                this.customEndDate = ""
              }
            }
          }
        } else if (filter.data_type === "date" && filter.keys.length > 1) {
          this.$set(this.dateFilterRadioValue, filter.keys[0], 'empty');
          this.$set(this.filterValues, filter.keys[0], "");
          this.$set(this.filterValues, filter.keys[1], "");
          this.$delete(this.dynamicDateCustomConfig, filter.keys[0]);
          this.$delete(this.dynamicDateFilterValues, filter.name);
        } else if (filter.data_type === "date") {
          this.$set(this.dateFilterRadioValue, filter.keys[0], 'empty');
          this.$set(this.filterValues, filter.keys[0], "");
          this.$delete(this.dynamicDateCustomConfig, filter.keys[0]);
          this.$delete(this.dynamicDateFilterValues, filter.name);
        } else {
          this.$set(this.filterValues, filter.keys[0], "");
        }
      });

      Object.keys(this.dynamicOptions).forEach(key => {
        this.$set(this.dynamicOptions, key, []);
      });

      Object.keys(this.searchInputs).forEach(key => {
        this.$set(this.searchInputs, key, "");
      });

      if (this.isQuestionnaireSelected) {
        this.$set(this.filterValues, 'template_ids', [this.selectedTemplateId]);
      }

      const filteredFilters = Object.fromEntries(
        Object.entries(this.filterValues).map(([key, value]) => {
          if (key === 'dynamic_filters') return null;
          if (Array.isArray(value)) {
            const filteredValue = value.filter(item => item !== "all");
            return filteredValue.length > 0 ? [key, filteredValue] : [key, []];
          } else if (typeof value === "string") {
            if (value === "all") {
              return [key, ""];
            }
            if (value === "") {
              return null;
            }
          }
          return [key, value];
        }).filter(Boolean)
      );
      filteredFilters.dynamic_filters = this.buildDynamicFiltersPayload();

      const apiParams = resolveDynamicFiltersToAbsolute(filteredFilters, this.filters);

      const payload = {
        id: this.reportTemplate.id,
        payload: {
          report_template: {
            filter: filteredFilters,
          }
        }
      };

      this.$store.dispatch('reportTemplates/updateReportTemplate', payload);
      setTimeout(() => {
        this.$store.dispatch('reportTemplates/getPreviewDataIds', { id: this.reportTemplate.id, params: apiParams });
        this.$store.dispatch('reportTemplates/getDataCount', { id: this.reportTemplate.id, params: apiParams });
      }, 500);
      this.$emit("close-menu");
    },

    hasSelectedValue(filter) {
      const key = filter.keys[0];
      if (filter.data_type === 'date') {
        if (this.getDynamicFilterValue(filter) || isDynamicFilter(this.filterValues[key])) return true;
        if (filter.keys.length > 1) {
          return this.filterValues[filter.keys[0]] && this.filterValues[filter.keys[1]];
        }
        return this.filterValues[key] && this.filterValues[key] !== "";
      }

      const value = this.filterValues[key];
      if (Array.isArray(value)) {
        return value.length > 0 && !(value.length === 1 && value[0] === "all");
      }
      return value && value !== "all" && value !== "";
    },

    getSelectedDisplayValue(filter) {
      const key = filter.keys[0];
      const value = this.filterValues[key];

      if (filter.data_type === 'date') {
        const dynamicValue = this.getDynamicFilterValue(filter);
        if (dynamicValue) {
          return getDynamicFilterLabel(dynamicValue, this.getDynamicPresetsForFilter(filter));
        }
        if (isDynamicFilter(value)) {
          return getDynamicFilterLabel(value, this.getDynamicPresetsForFilter(filter));
        }
        if (filter.keys.length === 1) {
          return this.formatDate(value);
        }
        const start = this.filterValues[filter.keys[0]];
        const end = this.filterValues[filter.keys[1]];
        return `${this.formatDate(start)} - ${this.formatDate(end)}`;
      }

      if (Array.isArray(value)) {
        return `${value.length} selected`;
      }

      if (filter.data_type === 'select' || filter.data_type === 'radio') {
        const option = (filter.data || []).find(opt => opt.id === value || opt === value);
        return option?.name || option?.name_code_format || value;
      }

      return value;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return moment(dateStr, "YYYY-MM-DD", true).format("M/D/YYYY");
    },

    clearFilterValue(filter) {
      const key = filter.keys[0];
      if (filter.data_type === 'checkbox') {
        this.$set(this.filterValues, key, []);
      } else if (filter.data_type === 'date' && filter.keys.length > 1) {
        this.$set(this.dateFilterRadioValue, key, 'empty');
        this.$set(this.filterValues, filter.keys[0], "");
        this.$set(this.filterValues, filter.keys[1], "");
        this.$delete(this.dynamicDateCustomConfig, key);
        this.$delete(this.dynamicDateFilterValues, filter.name);
      } else if (filter.data_type === 'date') {
        this.$set(this.dateFilterRadioValue, key, 'empty');
        this.$set(this.filterValues, key, "");
        this.$delete(this.dynamicDateCustomConfig, key);
        this.$delete(this.dynamicDateFilterValues, filter.name);
      } else {
        this.$set(this.filterValues, key, "");
      }
    },
    handleBusinessUnitChange(newBusinessUnitId, buKey, deptKey) {
      // Use the passed deptKey parameter, or fallback to computed departmentKey for backward compatibility
      const departmentKeyToUse = deptKey || this.departmentKey;
      const departmentFilter = this.filters.find(f => f.keys[0] === departmentKeyToUse);

      if (!(departmentFilter && Array.isArray(departmentFilter.data))) return;

      const selectedBuIds = Array.isArray(newBusinessUnitId)
        ? newBusinessUnitId.filter(id => id !== 'all').map(id => id.toString())
        : [];

      // When no BU is selected, keep departments empty
      if (selectedBuIds.length === 0) {
        this.$set(this.dynamicOptions, departmentKeyToUse, []);
        this.$set(this.filterValues, departmentKeyToUse, []);
        this.previousBuSelection = [];
        return;
      }

      const allDepartments = (departmentFilter.data || []).filter(dept => dept?.id !== 'all');
      const filteredDepartments = allDepartments.filter(dept =>
        selectedBuIds.includes(dept.business_unit_id?.toString())
      );

      this.$set(this.dynamicOptions, departmentKeyToUse, filteredDepartments);

      // On initial load, prioritize saved filter over auto-select
      const savedFilterValue = this.reportTemplate.filter?.[departmentKeyToUse];
      if (!this.initialDeptLoaded && Array.isArray(savedFilterValue) && savedFilterValue.length > 0) {
        const validSavedDepartments = savedFilterValue.filter(id =>
          filteredDepartments.some(dept => dept.id === id)
        );
        if (validSavedDepartments.length > 0) {
          this.$set(this.filterValues, departmentKeyToUse, validSavedDepartments);
          this.initialDeptLoaded = true;
          this.previousBuSelection = [...selectedBuIds];
          return;
        }
      }

      // Always sync departments with the current BU selection
      const nextDeptIds = filteredDepartments.map(dept => dept.id);
      this.$set(this.filterValues, departmentKeyToUse, nextDeptIds);

      this.previousBuSelection = [...selectedBuIds];
      this.initialDeptLoaded = true;
    },
  },
  created() {
    this.initializeFilterValues();
  },
  watch: {
    "filterValues.client_business_unit_id": {
      handler(newVal) {
        // Only handle if this report uses client_business_unit_id (non-Client reports)
        if (!this.isClientReport) {
          this.handleBusinessUnitChange(newVal, 'client_business_unit_id', 'client_department_id');
        }
      },
      immediate: true
    },
    "filterValues.business_unit_id": {
      handler(newVal) {
        // Only handle if this report uses business_unit_id (Client reports)
        if (this.isClientReport) {
          this.handleBusinessUnitChange(newVal, 'business_unit_id', 'department_id');
        }
      },
      immediate: true
    },
    // Watch for reportTemplate.filter changes to handle async data loading
    "reportTemplate.filter": {
      handler(newFilter) {
        if (!newFilter || this.initialDeptLoaded) return;

        const savedDeptIds = newFilter[this.departmentKey];
        if (Array.isArray(savedDeptIds) && savedDeptIds.length > 0) {
          const departmentFilter = this.filters.find(f => f.keys[0] === this.departmentKey);
          if (departmentFilter && Array.isArray(departmentFilter.data)) {
            const buValue = this.filterValues[this.businessUnitKey];
            const hasValidBu = buValue && Array.isArray(buValue) &&
              buValue.length > 0 && !buValue.includes('all');

            if (hasValidBu) {
              const filteredDepartments = departmentFilter.data.filter(dept =>
                dept?.id !== 'all' && buValue.includes(dept.business_unit_id)
              );
              const validSavedDepts = savedDeptIds.filter(id =>
                filteredDepartments.some(dept => dept.id === id)
              );
              if (validSavedDepts.length > 0) {
                this.$set(this.filterValues, this.departmentKey, validSavedDepts);
                this.initialDeptLoaded = true;
              }
            }
          }
        }
      },
      deep: true,
      immediate: true
    },
    selectedColumns: {
      handler(newVal) {
        let templateIds = this.isQuestionnaireSelected ? [this.selectedTemplateId] : [];

        const displayTemplateIds = templateIds.length === 0 ? ["all"] : templateIds;

        this.$set(this.filterValues, 'template_ids', displayTemplateIds);
      },
      deep: true,
    },
  },
};
</script>


<style lang="scss" scoped>
.column-filter {
  height: calc(100% - 38px);
  padding: 16px;
  margin: 16px;
  background-color: #FFF;
  border: 1px solid #D7D7D7;
  border-radius: 8px;

  .filter-item {
    background-color: #FFF;
    border: 1px solid #D7D7D7;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    height: 48px;
    cursor: pointer;

    &:hover {
      background-color: #F5F5F5;
    }
  }

  .filter-item-selected {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
    height: 48px;
  }

  .item-selected {
    border-radius: 8px 0 0 8px;
    background-color: #D4E7FF;
    padding: 0 8px 0 12px;
    height: 100%;
    min-width: 132px;
    max-width: max-content;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .item-selected span {
    font-size: 9px;
    color: #848484;
    text-transform: uppercase;
    font-weight: bold;
  }

  .item-selected h5 {
    font-size: 14px;
    color: #3F3F3F;
    margin: 0;
    margin-right: 6px;
  }

  .clear-filter {
    border-radius: 0 8px 8px 0;
    background-color: #D4E7FF;
    width: 32px;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    &:hover {
      background-color: #C0D9F7;
    }
  }

  .bg-blue-active {
    background-color: #EDF4FE !important;
  }

  .parent-group-header {
    font-weight: bold;
    font-size: 13px;
    color: #3F3F3F;
    padding: 8px 0 4px 0;
    border-bottom: 1px solid #E0E0E0;
    margin-bottom: 8px;
  }


}

::v-deep.v-list-item__action {
  margin-right: 10px !important;
}

::v-deep .filter-card {
  max-height: 500px;
  overflow: auto;
}

::v-deep .filter-card.date-filter-card {
  width: 370px;
  max-width: min(370px, calc(100vw - 32px)) !important;
}

::v-deep .filter-card .sticker {
  padding: 8px 16px;
  border-radius: 4px;
  margin-bottom: 8px;
}

::v-deep .custom-control-label.sticker::before,
.custom-control-label.sticker::after {
  top: 0.7rem !important;
}

::v-deep .v-select__selections {
  gap: 6px 0;
  padding: 8px 0;
}
</style>

<style>
.bg-blue-active {
  background-color: #EDF4FE !important;
}

.hover-light-blue:hover {
  background-color: #EDF4FE !important;
  cursor: pointer !important;
}
</style>
